import type { DocumentNode } from 'graphql';
import type { IGraphqlErrorPayload } from './graphqlRequestError';

/**
 * A web newer than its server (I-1383, `docs/concept-client-dataset-sync.md` § "A web newer than its
 * server").
 *
 * A bundle can ask for a field its server does not know yet: a rollout that starts the web first, a
 * backend rolled back alone, dev's preview, or an app that carries its own bundle. The server then
 * refuses the whole operation with a 400 (`GRAPHQL_VALIDATION_FAILED`, *Cannot query field "f" on
 * type "T"*), and a list that asked for one new field failed whole.
 *
 * That answer is the server saying, for exactly this operation and field, that the web is ahead of
 * it. So the read is asked again without the fields it named, found by the error's `locations`, and
 * the app says once that it is newer than its server. ⛔ Only a field is ever dropped: an unknown
 * argument, type or enum value would change what the operation means, so it fails as it did.
 *
 * ⛔ **And only a field that is nullable in the schema this web was built against** (the captain,
 * 2026-10-10). A view written against that schema already handles such a field being null or
 * absent. A non-null field is one the view's types promise a value for, so nothing is drawn
 * without it: the read fails as its own view's error, and the notice still says why.
 */

interface IGraphqlLocation {
  line: number;
  column: number;
}

interface IGraphqlValidationError extends IGraphqlErrorPayload {
  locations?: ReadonlyArray<IGraphqlLocation>;
}

const VALIDATION_FAILED = 'GRAPHQL_VALIDATION_FAILED';
const UNKNOWN_FIELD = /^Cannot query field "([^"]+)" on type "([^"]+)"/;
/** What only a server older than the web says about a web operation. */
const SERVER_LACKS = /^(?:Cannot query field|Unknown argument|Unknown type|Unknown directive)\b/;

/** How long a rewrite is used before the whole read is asked again, in case the server caught up. */
export const OLDER_SERVER_REWRITE_TTL_MS = 10 * 60_000;
/** Validation reports every unknown field at once; a second round covers one emptied fragment. */
const MAX_ROUNDS = 3;

/** The `errors` a GraphQL rejection carried: an axios 400's body, or the desktop transport's. */
export function validationErrorsOf(rejection: unknown): IGraphqlValidationError[] | null {
  const candidate = rejection as {
    response?: { status?: unknown; data?: unknown };
    status?: unknown;
    body?: unknown;
  } | null;
  const body = candidate?.response ? candidate.response.data : candidate?.body;
  const errors = (body as { errors?: unknown } | null | undefined)?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const valid = errors.filter(
    (error): error is IGraphqlValidationError =>
      Boolean(error) && typeof (error as { message?: unknown }).message === 'string',
  );
  return valid.some(isValidationFailure) ? valid : null;
}

function isValidationFailure(error: IGraphqlValidationError): boolean {
  return error.extensions?.code === VALIDATION_FAILED;
}

/** Whether the server refused this operation for naming something it does not have. */
export function saysServerIsBehind(errors: ReadonlyArray<IGraphqlValidationError>): boolean {
  return errors.some((error) => isValidationFailure(error) && SERVER_LACKS.test(error.message));
}

const locationKey = (line: number, column: number) => `${line}:${column}`;

/**
 * Whether every unknown field the server named is one the schema this web was built against
 * declares nullable. A field that schema declares non-null, or one the web's operations never
 * select, is never dropped.
 *
 * The answer is read from a map generated at build time for the fields the web selects
 * (`generated/selected-field-nullability.ts`), not from the schema: loading `schema.gql` put the
 * whole API schema in the public bundle (I-1391). The map is a chunk of its own, loaded only by a
 * page whose server is behind.
 */
export async function nullableInBuiltSchema(
  errors: ReadonlyArray<IGraphqlValidationError>,
): Promise<boolean> {
  const { SELECTED_FIELD_NULLABILITY } = await import('../generated/selected-field-nullability');
  return errors.every((error) => {
    const [, fieldName, typeName] = UNKNOWN_FIELD.exec(error.message) ?? [];
    return Boolean(
      fieldName &&
      typeName &&
      Object.prototype.hasOwnProperty.call(SELECTED_FIELD_NULLABILITY, typeName) &&
      SELECTED_FIELD_NULLABILITY[typeName].includes(fieldName),
    );
  });
}

/**
 * `query` without the fields `errors` named as unknown, or `null` when an omission cannot cure it:
 * any other error, an unknown field without a location, a field this web's schema does not declare
 * nullable, a location that names no field, or nothing left to ask. A selection left empty goes too, and so do fragments and variables nothing uses.
 */
export async function withoutUnknownFields(
  query: string,
  errors: ReadonlyArray<IGraphqlValidationError>,
  droppable: (
    errors: ReadonlyArray<IGraphqlValidationError>,
  ) => Promise<boolean> = nullableInBuiltSchema,
): Promise<string | null> {
  if (errors.length === 0) return null;
  if (!errors.every((error) => UNKNOWN_FIELD.test(error.message) && error.locations?.length)) {
    return null;
  }
  if (!(await droppable(errors))) return null;
  const wanted = new Set(
    errors.flatMap((error) =>
      (error.locations ?? []).map(({ line, column }) => locationKey(line, column)),
    ),
  );
  // The parser is loaded only by a page whose server is behind: no other page pays for it.
  const { Kind, parse, print, visit } = await import('graphql');

  let removed = 0;
  let document: DocumentNode = visit(parse(query), {
    Field: {
      enter(node) {
        const start = node.loc?.startToken;
        if (start && wanted.has(locationKey(start.line, start.column))) {
          removed += 1;
          return null;
        }
        return undefined;
      },
    },
  });
  if (removed !== wanted.size) return null;

  // Empty selections go, from the inside out; an emptied fragment takes its spreads with it.
  for (let round = 0; round < 10; round += 1) {
    const emptied = new Set<string>();
    for (const definition of document.definitions) {
      if (definition.kind === Kind.FRAGMENT_DEFINITION) {
        if (definition.selectionSet.selections.length === 0) emptied.add(definition.name.value);
      }
    }
    let changed = false;
    document = visit(document, {
      FragmentDefinition(node) {
        if (!emptied.has(node.name.value)) return undefined;
        changed = true;
        return null;
      },
      FragmentSpread(node) {
        if (!emptied.has(node.name.value)) return undefined;
        changed = true;
        return null;
      },
      Field: {
        leave(node) {
          if (!node.selectionSet || node.selectionSet.selections.length > 0) return undefined;
          changed = true;
          return null;
        },
      },
      InlineFragment: {
        leave(node) {
          if (node.selectionSet.selections.length > 0) return undefined;
          changed = true;
          return null;
        },
      },
    });
    if (!changed) break;
  }

  const operations = document.definitions.filter(
    (definition) => definition.kind === Kind.OPERATION_DEFINITION,
  );
  if (
    operations.length === 0 ||
    operations.some(
      (operation) =>
        operation.kind === Kind.OPERATION_DEFINITION &&
        operation.selectionSet.selections.length === 0,
    )
  ) {
    return null;
  }

  // Fragments no spread uses any more, and variables nothing reads, are errors of their own.
  const spread = new Set<string>();
  const read = new Set<string>();
  visit(document, {
    FragmentSpread(node) {
      spread.add(node.name.value);
    },
    VariableDefinition() {
      return false;
    },
    Variable(node) {
      read.add(node.name.value);
    },
  });
  document = visit(document, {
    FragmentDefinition(node) {
      return spread.has(node.name.value) ? undefined : null;
    },
    VariableDefinition(node) {
      return read.has(node.variable.name.value) ? undefined : null;
    },
  });
  return print(document);
}

// --- Said once: the app is newer than its server ---------------------------------------------

let serverBehind = false;
const listeners = new Set<() => void>();

/** The server refused something only a newer server has. Said once per page. */
export function reportServerBehind(): void {
  if (serverBehind) return;
  serverBehind = true;
  console.warn(
    '[featit] this app is newer than its server: it refused a read for naming something it lacks',
  );
  for (const listener of [...listeners]) listener();
}

export function isServerBehind(): boolean {
  return serverBehind;
}

export function subscribeToServerBehind(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// --- The request -----------------------------------------------------------------------------

const rewrites = new Map<string, { query: string; until: number }>();

/** Tests only: forget every rewrite and the notice. */
export function resetOlderServerForTests(): void {
  rewrites.clear();
  serverBehind = false;
}

/**
 * `send(query)`, asked again without the fields a server behind the web refused. The rewrite is
 * remembered, so the next run of the same read asks the server once. A rejection an omission
 * cannot cure is thrown exactly as it came.
 */
export async function requestTolerantOfOlderServer<T>(
  query: string,
  send: (query: string) => Promise<T>,
  now: () => number = Date.now,
): Promise<T> {
  const remembered = rewrites.get(query);
  let current = remembered && remembered.until > now() ? remembered.query : query;
  if (remembered && current === query) rewrites.delete(query);
  for (let round = 0; ; round += 1) {
    try {
      return await send(current);
    } catch (rejection) {
      const errors = validationErrorsOf(rejection);
      if (!errors) throw rejection;
      if (saysServerIsBehind(errors)) reportServerBehind();
      const next = round < MAX_ROUNDS ? await withoutUnknownFields(current, errors) : null;
      if (!next) throw rejection;
      rewrites.set(query, { query: next, until: now() + OLDER_SERVER_REWRITE_TTL_MS });
      current = next;
    }
  }
}
